import { KdsCheckboxGroup, KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { HttpStatusCode } from "axios";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import { redispatchQuantities } from "@/helpers/redispatchQuantities";
import { useOrderItems, useToasts } from "@/hooks";
import { type ModalPresetAttributesProps } from "./ModalPresetAttributes.types";

export const ModalPresetAttributes = ({
  isOpen,
  originalOrderItem,
  onOpenChange,
}: ModalPresetAttributesProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const [searchParams] = useSearchParams();

  // States
  const [presetDeliveryDate, setPresetDeliveryDate] = useState<boolean>(false);
  const [presetDispatch, setPresetDispatch] = useState<boolean>(false);

  // Form context
  const methods = useFormContext<OrderItemConfigurationSchema>();

  // Query Hooks
  const { useUpdateOrderItem } = useOrderItems();
  const { mutateAsync: updateOrderItem, isPending } = useUpdateOrderItem();

  // Values to preset
  const originalDispatcheTyoe = originalOrderItem.sandboxItem.dispatchType;
  const originalDispatches =
    originalOrderItem.sandboxItem.SandboxItemVersion[0]
      .SandboxItemVersionDispatch;
  const originalDeliveryDate = originalOrderItem.deliveryDate;

  // Handle Preset
  const handlePreset = () => {
    // Preset Dispatches and dispatch type
    if (presetDispatch) {
      methods.getValues("versions").forEach((version, versionIndex) => {
        const { quantity } = version.dispatches[0];

        methods.setValue(
          `versions.${versionIndex}.dispatches`,
          originalDispatches.map((originalDispatch, index) => ({
            quantity: index === 0 ? quantity : 0,
            address: {
              address: originalDispatch.address,
              recipientName: originalDispatch.recipientName,
              phoneNumber: originalDispatch.phoneNumber,
              postalCode: originalDispatch.postalCode,
              city: originalDispatch.city,
              complementaryInfo: originalDispatch.complementaryInfo,
              country: originalDispatch.country,
              name: originalDispatch.nameAddress ?? "",
            },
          }))
        );
      });

      methods.setValue("dispatchType", originalDispatcheTyoe);

      // Equaly dispatch quantities between versions and addresses
      redispatchQuantities({
        totalQuantities: methods.getValues("totalQuantity"),
        methods: methods,
      });
    }

    // Preset DeliveryDate
    if (presetDeliveryDate && originalDeliveryDate) {
      methods.setValue("deliveryDate", new Date(originalDeliveryDate));
    }
  };

  // Handle Submit
  const handleSubmit = async () => {
    // Update form data with preset data
    handlePreset();

    // Get form data
    const {
      deliveryDate,
      dispatchFiles: _dispatchFiles,
      preMediaFiles: _preMediaFiles,
      versions,
      ...rest
    } = methods.getValues();

    const orderItemId = searchParams.get("tabOrderItemId");

    if (!orderItemId) {
      return;
    }

    try {
      const res = await updateOrderItem({
        limitedRefetch: true,
        orderId: originalOrderItem.orderId,
        orderItemId,
        orderItemUpdateCommand: {
          ...rest,
          sandboxItemVersion: versions.map((version) => ({
            id: version.id,
            isDeleted: version.isDeleted,
            isHDFileDeleted: version.isHDFileDeleted,
            name: version.name,
            blobId: version.blobId,
            fileSignedId: version.fileSignedId,
            sandboxItemVersionDispatch: version.dispatches.map((dispatch) => ({
              quantity: dispatch.quantity,
              address: {
                address: dispatch.address.address,
                city: dispatch.address.city,
                country: dispatch.address.country,
                id: dispatch.address.id,
                name: dispatch.address.name,
                phoneNumber: dispatch.address.phoneNumber,
                postalCode: dispatch.address.postalCode,
                recipientName: dispatch.address.recipientName,
              },
            })),
          })),
          deliveryDate: deliveryDate
            ? new Date(deliveryDate).toISOString()
            : undefined,
        },
      });

      if (res.status === HttpStatusCode.Ok) {
        // Succes Toast
        add({
          state: "success",
          title: t("orderItemConfig.modal.presetAttributes.toastSuccess"),
        });

        // Close modal
        onOpenChange();
      }
    } catch {
      add({
        state: "error",
        title: t("orderItemConfig.modal.presetAttributes.toastError"),
      });
    }
  };

  return (
    <KdsModal
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          title: t("orderItemConfig.modal.presetAttributes.title"),
          description: t("orderItemConfig.modal.presetAttributes.subtitle"),
        },
        content: (
          <div>
            <KdsCheckboxGroup
              checkboxProps={[
                {
                  isChecked: presetDeliveryDate && presetDispatch,
                  onChange: () => {
                    setPresetDeliveryDate(
                      !(presetDeliveryDate && presetDispatch)
                    );
                    setPresetDispatch(!(presetDeliveryDate && presetDispatch));
                  },
                  label: t(
                    "orderItemConfig.modal.presetAttributes.checkboxAll"
                  ),
                },
                {
                  isChecked: presetDeliveryDate,
                  onChange: () => setPresetDeliveryDate(!presetDeliveryDate),
                  label: t(
                    "orderItemConfig.modal.presetAttributes.checkboxDeliveryDate"
                  ),
                },
                {
                  isChecked: presetDispatch,
                  onChange: () => setPresetDispatch(!presetDispatch),
                  label: t(
                    "orderItemConfig.modal.presetAttributes.checkboxDispatch"
                  ),
                },
              ]}
              label={t("orderItemConfig.modal.presetAttributes.contentTitle")}
            />
          </div>
        ),
        dialogFooterProps: {
          type: "double",
          buttonProps: [
            {
              variant: "outlined",
              color: "neutral",
              children: t("buttons.no"),
              onPress: onOpenChange,
            },

            {
              children: t("buttons.applySelection"),
              isLoading: isPending,
              onPress: () => void handleSubmit(),
            },
          ],
        },
      }}
      isOpen={isOpen && !!originalDeliveryDate && !!originalDispatches}
      onOpenChange={() => {
        onOpenChange();
      }}
    />
  );
};
